/**
 * /ja/product-roadmap — プロダクトロードマップ concept page (JA).
 * Links /ja/product-discovery + /ja/decision-log.
 */
import { MarketingShell } from './MarketingShell';

export function JaProductRoadmap() {
  return (
    <MarketingShell
      currentLocale="ja"
      hreflangPath="/product-roadmap"
      hreflangLocales={['ja']}
      documentTitle="プロダクトロードマップとは｜作り方・テンプレ・例 · Draftlize"
      documentDescription="プロダクトロードマップの作り方を、形式・例・テンプレート付きで解説。スコープ変更に強い「生きたロードマップ」の考え方まで紹介します。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">ガイド · ロードマップ</span>
          <h1 className="serif" style={{ fontSize: 'clamp(32px, 4.4vw, 54px)', lineHeight: 1.08, margin: '12px 0 24px' }}>
            スコープ変更に強い<br />
            <em>プロダクトロードマップ。</em>
          </h1>
          <p className="lede">
            プロダクトロードマップは、製品が<em>どこへ</em>向かうか、そして<em>なぜ</em>かを示すものです。細部ではなく、方向と順序。難しいのは作ることより、それが「固定されたスライド」で終わること——3週間後に決定が変わっても、ロードマップは変わらず、誰も信じなくなる。ここでは形式と例、そしてロードマップを決定につないで「本当のまま」に保つ方法を解説します。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">機能するロードマップの形式。</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>Now / Next / Later</strong>——偽りの日付精度を避けられ、初期に最適。</li>
            <li><strong>テーマ／アウトカム型</strong>——機能ではなく目標で並べる。</li>
            <li><strong>時間軸（四半期）</strong>——依存で期日が固定される場合に。</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            <strong>ミニ例（Now/Next/Later）:</strong> Now — オンボーディングの摩擦を下げる · Next — セルフサーブ課金 · Later — チームワークスペース。各項目には<em>なぜ</em>——それをロードマップに載せた決定——が付きます。スライド版で失われるのが、まさにこの「なぜ」です。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">固定 vs 生きている</span>
            <h2 className="serif">スライドのロードマップ <em>vs 決定とともに動くロードマップ。</em></h2>
          </div>
          <p>ロードマップは、決定の連なりの「見える先端」にすぎません。両者を切り離すと、静かに古くなります。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">つながったロードマップ（Draftlize）</th>
              <th>スライドのロードマップ</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>各項目の「なぜ」</td><td className="ours yes">決定カードにリンク</td><td className="no">頭の中／散在</td></tr>
            <tr><td>決定が変わったとき</td><td className="ours yes">該当項目が stale に</td><td className="no">ロードマップは古いまま</td></tr>
            <tr><td>3週間後</td><td className="ours yes">最新か明示</td><td className="no">誰も信じない</td></tr>
            <tr><td>AI エージェントが使える</td><td className="ours yes">MCP で文脈を読む</td><td className="partial">平らなスライド</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          多くのロードマップは、計画が間違っていたのではない。最後の決定から3週間、誰も追従させなかっただけだ。
        </blockquote>
        <cite>ロードマップを決定につなげば、決定が変わったとき自動で追従する。それが Draftlize。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">よくある質問。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'ロードマップとバックログの違いは？', a: 'ロードマップはアウトカム単位で方向と順序を示し、バックログは具体的な作業の優先順位リストです。ロードマップは「なぜ／どこへ」、バックログは「次は何を」に答えます。' },
            { q: '期間の目安は？', a: '誠実に方向を示せる範囲まで。固定の四半期より Now/Next/Later が有効なことが多いです。偽りの日付精度が、ロードマップの信頼を損なう最大の原因です。' },
            { q: '変更にどう対応する？', a: '項目の裏の決定が変われば、項目も変わるべきです。Draftlize なら両者がリンクし、変わった決定が該当ロードマップ部分を自動で stale にします。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.9, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
        <p style={{ maxWidth: 820, margin: '8px auto 0', fontSize: 15, color: 'var(--ink-1)' }}>
          関連： <a href="/ja/product-discovery" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>プロダクトディスカバリー</a> · <a href="/ja/decision-log" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>意思決定ログ</a>
        </p>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">追従するロードマップを<em>——$5 無料で。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新規登録で $5 付与</span>
          <span>使った分だけ支払い</span>
          <span>残高は無期限</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            ロードマップをスライドではなく、つながったカードで。決定が変われば、ロードマップも自動で追従します——3週間後ではなく。
          </p>
          <a className="btn-primary" href="/">$5 無料で始める</a>
        </div>
      </section>
    </MarketingShell>
  );
}
